import { dashboardServices } from '@/services/dashboard.service';
import { queryKeys } from '@/services/queryKeyFactory';
import { useQuery } from '@tanstack/react-query';
import React, { useContext, useEffect, useState } from 'react';
import { useCompset } from './CompsetContext';
import { usePlans } from './PlanRulesContext';

export const DashboardContext = React.createContext();

function DashboardProvider({ children }) {

    const [activeView, setActiveView] = useState('market-insights');
    const { activeCompset } = useCompset();
    const { canAccessAdvanceAnalysis, canAccessRateParity, canAccessAiInsights } = usePlans();

    const { data: marketInsightData, isLoading: isLoadingMarketInsight, isError: isErrorMarketInsight, isEnabled: isEnabledMarketInsight } = useQuery({
        queryKey: queryKeys.marketInsights(activeCompset?.compset_id),
        queryFn: () => dashboardServices.getMarketInsights(activeCompset?.compset_id),
        enabled: !!activeCompset,
    })

    const { data: competitiveAnalysisData, isLoading: isLoadingCompetitiveAnalysisData, isError: isErrorCompetitiveAnalysisData, isEnabled: isEnabledCompetitiveAnalysisData } = useQuery({
        queryKey: queryKeys.competitiveAnalysis(activeCompset?.compset_id),
        queryFn: () => dashboardServices.getCompetitiveAnalysis(activeCompset?.compset_id),
        enabled: !!activeCompset
    })

    const { data: rateparity, isLoading: isLoadingRateparity, isError: isErrorRateparity, isEnabled: isEnabledRateparity } = useQuery({
        queryKey: queryKeys.rateparity(activeCompset?.compset_id),
        queryFn: () => dashboardServices.getRateParity(activeCompset?.compset_id),
        enabled: !!activeCompset && canAccessRateParity()
    })

    const { data: advancedAnalytics, isLoading: isLoadingAdvancedAnalytics, isError: isErrorAdvancedAnalytics, isEnabled: isEnabledAdvancedAnalytics } = useQuery({
        queryKey: queryKeys.advancedAnalytics(activeCompset?.compset_id),
        queryFn: () => dashboardServices.getAdvancedAnalysis(activeCompset?.compset_id),
        enabled: !!activeCompset && canAccessAdvanceAnalysis()
    })

    const { data: officialSiteInfo, isLoading: isLoadingOfficialSiteInfo, isError: isErrorOfficialSiteInfo, isEnabled: isEnabledOfficialSiteInfo, error } = useQuery({
        queryKey: queryKeys.officialSiteInfo(activeCompset?.compset_id),
        queryFn: () => dashboardServices.getRateparityOfficialSiteInfo(activeCompset?.compset_id),
        enabled: false
    })

    const { data: allAiInsights, isLoading: isLoadingAiInsights, isError: isErrorAiInsights } = useQuery({
        queryKey: queryKeys.allAiInsights(activeCompset?.compset_id),
        queryFn: () => dashboardServices.getAllAiInsights(activeCompset?.compset_id),
        enabled: !!activeCompset && canAccessAiInsights()
    })

    const value = {
        marketInsightData,
        isLoadingMarketInsight,
        isErrorMarketInsight,
        isEnabledMarketInsight,
        competitiveAnalysisData,
        isLoadingCompetitiveAnalysisData,
        isErrorCompetitiveAnalysisData,
        isEnabledCompetitiveAnalysisData,
        rateparity,
        isLoadingRateparity,
        isErrorRateparity,
        isEnabledRateparity,
        advancedAnalytics,
        isLoadingAdvancedAnalytics,
        isErrorAdvancedAnalytics,
        isEnabledAdvancedAnalytics,
        officialSiteInfo,
        isLoadingOfficialSiteInfo,
        isErrorOfficialSiteInfo,
        isEnabledOfficialSiteInfo,
        allAiInsights,
        isLoadingAiInsights,
        isErrorAiInsights,
        activeView,
        setActiveView
    };

    return (
        <DashboardContext.Provider value={value}>
            {children}
        </DashboardContext.Provider>
    );
}


export const useDashboard = () => {
    const context = useContext(DashboardContext);
    if (!context) {
        throw new Error("useDashboard must be used within DashboardProvider");
    }
    return context
}

export default DashboardProvider;